
import type { FC } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import FeedNavbar from 'components/UI/FeedNavbar/FeedNavbar'
import { StoriesList } from 'components-ref/organisms'
import { HomeFeed } from 'components-ref/templates'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import SuggestedList from 'features/SuggestedList/SuggestedList'
import { useStoryFeed } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { refreshSubscriptionListsCache } from 'services/subscriptionslists/subscriptionListsCache'
import { useAccessToken, useUserProfile } from 'store'

import { Auth } from '../auth'

import './_feed.scss'

export const Feed: FC = () => {
  const { t } = useTranslation()
  const { setTitle } = usePageTitle()

  const isAuthenticated = useAccessToken() !== ''
  const userData = useUserProfile()

  const scrollRef = useRef<HTMLDivElement>(null)

  const [listId, setListId] = useState<string>('all')
  const [homeSettled, setHomeSettled] = useState(false)

  const {
    reorderedStoryPosts = [],
    isSuccess: storySuccess,
    isError: storyError
  } = useStoryFeed()

  const hasStories = reorderedStoryPosts.length > 0
  const storySettled = storySuccess || storyError

  const onHomeSettled = useCallback(() => {
    setHomeSettled(true)
  }, [])

  // Page title
  useEffect(() => {
    setTitle(t('following'))
  }, [setTitle, t])

  // Background subscription lists refresh
  // Only runs after HomeFeed and StoryFeed finish their initial requests.
  // The refresh service skips requests when the Zustand cache is fresh.
useEffect(() => {
  console.log('[SubscriptionLists] Feed gate', {
    isAuthenticated,
    userId: userData?.id,
    homeSettled,
    storySettled
  })

  if (!isAuthenticated || !userData?.id) return
  if (!homeSettled || !storySettled) return

  console.log('[SubscriptionLists] Both feeds settled, scheduling refresh')

  const timer = window.setTimeout(() => {
    console.log('[SubscriptionLists] Starting cache refresh check')

    void refreshSubscriptionListsCache(
      'recently_added',
      'asc'
    )
      .then(() => {
        console.log('[SubscriptionLists] Refresh check completed')
      })
      .catch((error: any) => {
        console.error('[SubscriptionLists] Refresh failed:', error)
      })
  }, 500)

  return () => window.clearTimeout(timer)
}, [
  isAuthenticated,
  userData?.id,
  homeSettled,
  storySettled
])

  const headerSection: JSX.Element = hasStories ? (
    <>
      <div className="stories-title">Stories</div>
      <StoriesList
        page="feed"
        stories={reorderedStoryPosts}
      />
    </>
  ) : (
    <></>
  )

  if (!isAuthenticated || !userData) {
    return <Auth />
  }

  return (
    <>
      <div className="home" ref={scrollRef}>
        <WithHeaderSection
          customClass="home"
          headerSection={headerSection}
        >
          <FeedNavbar
            listId={listId}
            setListId={setListId}
          />

          <HomeFeed
            scrollContainerRef={scrollRef}
            listId={listId}
            onInitialLoadSettled={onHomeSettled}
          />
        </WithHeaderSection>
      </div>

      <DesktopAdditionalContent>
        <SuggestedList />
      </DesktopAdditionalContent>
    </>
  )
}

export default Feed
